import type { SubmissionReadOverride } from "./components/SubmissionsInformation/types";
import {
  LOR_TODO_IDS,
  NON_QUESTION_FIELDS,
  RECOMMENDER_EMAIL_FIELD,
  RECOMMENDER_NAME_FIELD,
  RECOMMENDER_ORGANIZATION_FIELD,
  RECOMMENDER_PHONE_FIELD,
  RECOMMENDER_TITLE_FIELD,
} from "./constants";
import type {
  FormioComponent,
  FormioSubmission,
  LetterOfRecommendationRequest,
  QuestionAnswer,
  RecommenderCard,
  RequestedRecommender,
} from "./types";

export const isLorTodo = (todoId: string | undefined): boolean =>
  !!todoId && LOR_TODO_IDS.includes(todoId.toLowerCase());

export const collectQuestionLabels = (components: FormioComponent[] | undefined): Record<string, string> => {
  const labels: Record<string, string> = {};
  for (const component of components ?? []) {
    if (component.key && component.label) {
      labels[component.key] = component.label;
    }
    Object.assign(labels, collectQuestionLabels(component.components));
    for (const column of component.columns ?? []) {
      Object.assign(labels, collectQuestionLabels(column.components));
    }
  }
  return labels;
};

const normalizeEmail = (email: string | undefined): string => (email ?? "").trim().toLowerCase();

const timeOf = (date: string | undefined): number => {
  const parsed = date ? new Date(date).getTime() : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const byNewest = (a: string | undefined, b: string | undefined): number => timeOf(b) - timeOf(a);

const humanizeKey = (key: string): string => {
  const spaced = key.replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const textOf = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number") {
    return String(value);
  }
  return "";
};

const fieldOf = (response: FormioSubmission, field: string): string => textOf(response.data?.[field]);

const isLetterOfRecommendationRequest = (value: unknown): value is LetterOfRecommendationRequest =>
  typeof value === "object" && value !== null;

const recommendersIn = (
  submissionData: SubmissionReadOverride["submission_data"] | undefined,
): LetterOfRecommendationRequest[] => {
  const requested = submissionData?.letter_of_recommendation;
  return Array.isArray(requested) ? requested.filter(isLetterOfRecommendationRequest) : [];
};

export const getRequestedRecommenders = (
  submissions: SubmissionReadOverride[] | undefined,
): RequestedRecommender[] => {
  const byEmail = new Map<string, RequestedRecommender>();

  for (const submission of submissions ?? []) {
    for (const request of recommendersIn(submission.submission_data)) {
      const email = normalizeEmail(request.email);
      const existing = byEmail.get(email);
      const isNewer = !existing || timeOf(existing.requestedAt) < timeOf(submission.created_at);
      if (email && isNewer) {
        byEmail.set(email, {
          name: [request.first_name, request.last_name].filter(Boolean).join(" "),
          email: request.email ?? "",
          requestedAt: submission.created_at,
        });
      }
    }
  }

  return [...byEmail.values()];
};

export const toQuestionAnswers = (
  response: FormioSubmission,
  questionLabels: Record<string, string>,
): QuestionAnswer[] => {
  const answers: QuestionAnswer[] = [];
  for (const [key, value] of Object.entries(response.data ?? {})) {
    const answer = textOf(value);
    if (answer && !NON_QUESTION_FIELDS.includes(key)) {
      answers.push({ key, question: questionLabels[key] ?? humanizeKey(key), answer });
    }
  }
  return answers;
};

const toAnsweredCard = (
  response: FormioSubmission,
  requests: RequestedRecommender[],
  questionLabels: Record<string, string>,
): RecommenderCard => {
  const email = fieldOf(response, RECOMMENDER_EMAIL_FIELD);
  const request = requests.find((candidate) => normalizeEmail(candidate.email) === normalizeEmail(email));

  return {
    id: response._id,
    name: fieldOf(response, RECOMMENDER_NAME_FIELD) || request?.name || email,
    title: fieldOf(response, RECOMMENDER_TITLE_FIELD) || undefined,
    organization: fieldOf(response, RECOMMENDER_ORGANIZATION_FIELD) || undefined,
    email,
    phone: fieldOf(response, RECOMMENDER_PHONE_FIELD) || undefined,
    submittedAt: response.created,
    requestedAt: request?.requestedAt,
    answers: toQuestionAnswers(response, questionLabels),
    isAwaiting: false,
  };
};

const toAwaitingCard = (request: RequestedRecommender): RecommenderCard => {
  const email = normalizeEmail(request.email);

  return {
    id: email,
    name: request.name || email,
    email,
    requestedAt: request.requestedAt,
    answers: [],
    isAwaiting: true,
  };
};

export const buildRecommenderCards = (
  requests: RequestedRecommender[],
  responses: FormioSubmission[],
  questionLabels: Record<string, string> = {},
): RecommenderCard[] => {
  const requestedEmails = new Set(requests.map((request) => normalizeEmail(request.email)));
  const answered = [...responses]
    .filter((response) => requestedEmails.has(normalizeEmail(fieldOf(response, RECOMMENDER_EMAIL_FIELD))))
    .sort((a, b) => byNewest(a.created, b.created))
    .map((response) => toAnsweredCard(response, requests, questionLabels));

  const answeredEmails = new Set(answered.map((card) => normalizeEmail(card.email)));
  const awaiting = requests
    .filter((request) => !answeredEmails.has(normalizeEmail(request.email)))
    .sort((a, b) => byNewest(a.requestedAt, b.requestedAt))
    .map(toAwaitingCard);

  return [...answered, ...awaiting];
};
